import { useState } from 'react';
import { motion } from 'framer-motion';
import { Instagram, Send } from 'lucide-react';
import { toast } from '../components/ui/sonner';
import { Reveal } from './Reveal';

const FORMSPREE_URL = 'https://formspree.io/f/xrpznlyl';

export const Contact = () => {
    const [nombre, setNombre] = useState('');
    const [numero, setNumero] = useState('');
    const [sending, setSending] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            const res = await fetch(FORMSPREE_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ nombre, numero }),
            });
            if (!res.ok) throw new Error('send failed');
            toast.success('¡Listo! Me pondré en contacto contigo muy pronto.');
            setNombre('');
            setNumero('');
        } catch {
            toast.error('No se pudo enviar. Inténtalo de nuevo o mándame un DM en Instagram.');
        } finally {
            setSending(false);
        }
    };

    return (
        <section id="contacto" className="relative py-28 px-6 bg-ink text-paper text-center overflow-hidden" data-testid="contact-section">
            <div
                className="absolute -bottom-56 -right-36 w-[500px] h-[500px] rounded-full opacity-25 pointer-events-none"
                style={{ background: 'radial-gradient(circle, #7A1228 0%, transparent 70%)' }}
            />
            <motion.span
                className="absolute left-[10%] top-16 text-roselight/40 text-xl pointer-events-none"
                animate={{ y: [0, -12, 0] }}
                transition={{ duration: 4.5, repeat: Infinity, ease: 'easeInOut' }}
            >
                ✦
            </motion.span>
            <motion.span
                className="absolute right-[16%] bottom-24 text-roselight/30 text-sm pointer-events-none"
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 5.5, repeat: Infinity, ease: 'easeInOut', delay: 1.2 }}
            >
                ✦
            </motion.span>
            <div className="relative z-10 max-w-[1080px] mx-auto">
                <Reveal>
                    <h2 className="font-serif font-light text-[clamp(2rem,5vw,3.2rem)] mb-4" data-testid="contact-title">
                        ¿Empezamos?
                    </h2>
                    <p className="text-[#D8D5D0] font-light text-lg max-w-[480px] mx-auto">
                        Déjame tu nombre y tu número, y yo me pongo en contacto contigo.
                    </p>
                </Reveal>
                <Reveal delay={0.15}>
                    <form onSubmit={submit} data-testid="contact-form" className="max-w-[420px] mx-auto flex flex-col gap-4 mt-9">
                        <input
                            data-testid="contact-name-input"
                            type="text"
                            required
                            maxLength={100}
                            value={nombre}
                            onChange={(e) => setNombre(e.target.value)}
                            placeholder="Tu nombre"
                            className="w-full bg-paper/[0.06] border border-paper/20 rounded-xl px-5 py-4 text-paper placeholder:text-paper/40 focus:border-roselight outline-none transition-colors"
                        />
                        <input
                            data-testid="contact-phone-input"
                            type="tel"
                            required
                            maxLength={30}
                            value={numero}
                            onChange={(e) => setNumero(e.target.value)}
                            placeholder="Tu número (WhatsApp)"
                            className="w-full bg-paper/[0.06] border border-paper/20 rounded-xl px-5 py-4 text-paper placeholder:text-paper/40 focus:border-roselight outline-none transition-colors"
                        />
                        <button
                            data-testid="contact-submit-button"
                            type="submit"
                            disabled={sending}
                            className="inline-flex items-center justify-center gap-2.5 rounded-full bg-rose text-paper px-8 py-4 text-base font-medium tracking-wide shadow-lg shadow-rose/30 hover:bg-roselight hover:-translate-y-px hover:shadow-xl active:scale-95 transition disabled:opacity-60"
                        >
                            <Send size={17} /> {sending ? 'Enviando…' : 'Enviar'}
                        </button>
                    </form>
                </Reveal>
                <Reveal delay={0.25} className="mt-10">
                    <p className="text-sm text-[#9C9994] mb-4">o si prefieres, mándame un DM en Instagram</p>
                    <a
                        href="https://www.instagram.com/adrihernanndez"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="contact-instagram-link"
                        className="inline-flex items-center gap-2.5 rounded-full border border-paper/40 text-paper px-8 py-4 text-base font-medium hover:border-roselight hover:text-roselight active:scale-95 transition"
                    >
                        <Instagram size={18} /> @adrihernanndez
                    </a>
                </Reveal>
            </div>
        </section>
    );
};
